<!-- src/views/CandidateProfile.vue -->
<template>
    <Header />
  <div class="candidate-profile-bg">
    <v-container fluid class="candidate-profile-root">
      <v-row class="profile-row">
        <!-- SIDEBAR -->
        <v-col cols="12" md="4" lg="3" class="profile-sidebar">
          <div class="sidebar-card">
            <!-- Profile top background -->
            <div class="sidebar-bg">
              <v-avatar size="76" class="sidebar-avatar">
                <v-icon size="48" color="primary">mdi-camera-outline</v-icon>
              </v-avatar>
            </div>
            <div class="sidebar-content">
              <div class="sidebar-name">
                {{ user?.fullName || "Your Name" }}
              </div>
              <div class="sidebar-progress-label">
                {{ remainingSectionsText }}
              </div>
              <v-progress-linear
                :model-value="progress"
                height="7"
                color="primary"
                class="sidebar-progress mb-4"
                rounded
              ></v-progress-linear>
              <div class="sidebar-sections">
                <SidebarSection
                  icon="mdi-briefcase-outline"
                  label="Experience"
                  :done="profile?.experience?.length"
                />
                <SidebarSection
                  icon="mdi-chart-timeline-variant-shimmer"
                  label="Skills"
                  :done="profile?.skills?.length"
                />
                <SidebarSection
                  icon="mdi-file-document-outline"
                  label="Resume"
                  :done="!!profile?.resumeMeta"
                />
                <SidebarSection
                  icon="mdi-microphone-outline"
                  label="Digital Profile"
                  :done="!!profile?.audioMeta || !!profile?.videoMeta"
                />
                <SidebarSection
                  icon="mdi-certificate-outline"
                  label="Licences or Certificates"
                />
                <SidebarSection
                  icon="mdi-briefcase-variant-outline"
                  label="Portfolio"
                />
                <SidebarSection
                  icon="mdi-account-check-outline"
                  label="Reference"
                />
              </div>
            </div>
          </div>
        </v-col>

        <!-- MAIN PANEL -->
        <v-col cols="12" md="8" lg="9" class="profile-main">
          <v-row>
            <v-col cols="12">
              <!-- Basic Info Card -->
              <ProfileCard
                section-title="Basic Info"
                @edit="showBasicInfoModal = true"
                :has-data="!!profile.basicInfo"
                @add="showBasicInfoModal = true"
              >
                <template #content>
                  <v-row>
                    <v-col cols="6" sm="6">
                      <div>
                        <InfoItem
                          icon="mdi-account-outline"
                          :label="profile?.basicInfo?.email || 'Add Email'"
                        />
                      </div>
                      <div>
                        <InfoItem
                          v-if="profile?.basicInfo?.visaStatus"
                          icon="mdi-card-account-details-outline"
                          :label="profile.basicInfo.visaStatus"
                        />
                        <AddInline
                          v-else
                          text="Add Visa Status"
                          @click="showBasicInfoModal = true"
                        />
                      </div>

                      <div>
                        <InfoItem
                          v-if="profile?.basicInfo?.language"
                          icon="mdi-translate"
                          :label="profile.basicInfo.language"
                        />

                        <AddInline
                          v-else
                          text="Add Languages"
                          @click="showBasicInfoModal = true"
                        />
                      </div>
                    </v-col>
                    <v-col cols="6" sm="6">
                      <div class="">
                        <InfoItem
                          icon="mdi-phone-outline"
                          :label="profile?.basicInfo?.phoneNumber || 'N/A'"
                        />
                      </div>

                      <div class="">
                        <InfoItem
                          v-if="profile?.basicInfo?.gender"
                          icon="mdi-gender-male-female"
                          :label="profile.basicInfo.gender"
                        />
                        <AddInline
                          v-else
                          text="Add Gender"
                          @click="showBasicInfoModal = true"
                        />
                      </div>
                      <div class="info-row">
                        <InfoItem
                          v-if="profile?.basicInfo?.nationality"
                          icon="mdi-flag-outline"
                          :label="profile.basicInfo.nationality"
                        />
                        <AddInline
                          v-else
                          text="Add Nationality"
                          @click="showBasicInfoModal = true"
                        />
                      </div>
                    </v-col>
                  </v-row>
                </template>
              </ProfileCard>
            </v-col>
            <!-- Repeat: Each section below in its own card just like above -->
            <v-col cols="12">
              <!-- Qualifications -->
              <ProfileCard
                section-title="Qualifications"
                @edit="showQualificationsModal = true"
                @add="showQualificationsModal = true"
                :has-data="
                  !!profile.qualification &&
                  Object.keys(profile.qualification).length
                "
              >
                <template #content>
                  <div
                    v-if="
                      profile?.qualification &&
                      Object.keys(profile.qualification).length
                    "
                  >
                    <div class="q-degree">
                      {{ profile.qualification.specialization }}
                    </div>
                    <div class="q-university">
                      {{ profile.qualification.university }}
                    </div>
                    <div class="q-dates">
                      {{ profile.qualification.graduation_start }} -
                      {{ profile.qualification.graduation_end }}
                    </div>
                    <div class="q-country">
                      {{ profile.qualification.qualification_country }}
                    </div>
                  </div>
                  <AddInline
                    v-else
                    text="Add Qualification"
                    @click="showQualificationsModal = true"
                  />
                </template>
              </ProfileCard>
            </v-col>
            <v-col cols="12">
              <!-- Experience -->
              <ProfileCard
                section-title="Experience"
                @edit="showExperienceModal = true"
                @add="showExperienceModal = true"
                :has-data="profile.experience?.length"
              >
                <template #content>
                  <div v-if="profile.experience?.length">
                    <div
                      v-for="exp in profile.experience"
                      :key="exp.id"
                      class="exp-item"
                    >
                      <span class="exp-title">{{ exp.jobTitle || "---" }}</span>
                      at {{ exp.company || "---" }}
                    </div>
                  </div>
                  <AddInline
                    v-else
                    text="Add Experience"
                    @click="showExperienceModal = true"
                  />
                </template>
              </ProfileCard>
            </v-col>
            <v-col cols="12">
              <!-- Skills -->
              <ProfileCard
                section-title="Skills"
                @edit="showSkillsModal = true"
                @add="showSkillsModal = true"
                :has-data="profile.skills?.length"
              >
                <template #content>
                  <v-chip-group column>
                    <v-chip
                      v-for="(skill, idx) in profile.skills"
                      :key="idx"
                      color="primary"
                      variant="outlined"
                      class="mr-2 mb-2"
                    >
                      {{ skill }}
                    </v-chip>
                  </v-chip-group>
                  <AddInline
                    v-if="!profile.skills?.length"
                    text="Add Skills"
                    @click="showSkillsModal = true"
                  />
                </template>
              </ProfileCard>
            </v-col>
            <v-col cols="12">
              <!-- Resume -->
              <!-- <ProfileCard
                section-title="Resume"
                @edit="showResumeModal = true"
                @add="showResumeModal = true"
                :has-data="!!profile.resume"
              >
                <template #content>
                  <div v-if="profile.resume">
                    <v-icon color="green">mdi-file-pdf-box</v-icon>
                    <a :href="profile.resumeUrl" target="_blank"
                      >Download Resume</a
                    >
                  </div>
                  <AddInline
                    v-else
                    text="Upload Resume"
                    @click="showResumeModal = true"
                  />
                </template>
              </ProfileCard> -->

              <ProfileCard
                section-title="Resume"
                @add="showResumeModal = true"
                @edit="showResumeModal = true"
                :has-data="!!profile.resumeMeta"
              >
                <template #content>
                  <div v-if="resumeUrl">
                    <v-icon color="green">mdi-file-pdf-box</v-icon>
                    <a :href="resumeUrl" target="_blank">Download Resume</a>
                  </div>
                  <AddInline
                    v-else
                    text="Upload Resume"
                    @click="showResumeModal = true"
                  />
                </template>
              </ProfileCard>
            </v-col>
            <v-col cols="12">
              <!-- Digital Profile -->
              <ProfileCard
                section-title="Digital Profile"
                @add="showDigitalProfileModal = true"
                @edit="showDigitalProfileModal = true"
                :has-data="!!profile.audioMeta || !!profile.videoMeta"
              >
                <template #content>
                  <div class="info-row">
                    <!-- Audio Intro -->
                    <div>
                      <div class="mb-2">Audio Intro</div>
                      <audio
                        v-if="audioIntroUrl"
                        :src="audioIntroUrl"
                        controls
                      />
                      <span v-else class="text-muted">N/A</span>
                    </div>

                    <!-- Video Intro -->
                    <div>
                      <div class="mb-2">Video Intro</div>
                      <video
                        v-if="videoIntroUrl"
                        :src="videoIntroUrl"
                        controls
                        style="max-width: 120px"
                      />
                      <span v-else class="text-muted">N/A</span>
                    </div>
                  </div>

                  <!-- If neither exists, show an Add prompt -->
                  <AddInline
                    v-if="!audioIntroUrl && !videoIntroUrl"
                    text="Add Digital Profile"
                    @click="showDigitalProfileModal = true"
                  />
                </template>
              </ProfileCard>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <!-- MODALS -->
      <BasicInfoModal v-model="showBasicInfoModal" />
      <QualificationsModal v-model="showQualificationsModal" />
      <ExperienceModal v-model="showExperienceModal" />
      <SkillsModal v-model="showSkillsModal" />
      <ResumeModal v-model="showResumeModal" />
      <DigitalProfileModal v-model="showDigitalProfileModal" />
    </v-container>
  </div>

  <FooterComponent />
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import Header from "@/components/Header.vue";
import FooterComponent from "@/components/footer/FooterComponent.vue";
import ProfileService from "@/services/profile.service";
import IndustryService from "@/services/industry.service";
import BasicInfoModal from "@/components/Jobs/Profile/Candidate/BasicInfoModal.vue";
import QualificationsModal from "@/components/Jobs/Profile/Candidate/QualificationsModal.vue";
import ExperienceModal from "@/components/Jobs/Profile/Candidate/ExperienceModal.vue";
import SkillsModal from "@/components/Jobs/Profile/Candidate/SkillsModal.vue";
import ResumeModal from "@/components/Jobs/Profile/Candidate/ResumeModal.vue";
import DigitalProfileModal from "@/components/Jobs/Profile/Candidate/DigitalProfileModal.vue";

import ProfileCard from "@/components/Jobs/Profile/Candidate/More/ProfileCard.vue";
import SidebarSection from "@/components/Jobs/Profile/Candidate/More/SidebarSection.vue";
import InfoItem from "@/components/Jobs/Profile/Candidate/More/InfoItem.vue";
import AddInline from "@/components/Jobs/Profile/Candidate/More/AddInline.vue";

/**
 * This view assumes:
 * - Your Vuex “profile” module has a mutation/action named `setCandidateProfile(payload)`
 *   that splits the returned object into basicInfo, qualification, experience, skills, resume, audioIntro, videoIntro, etc.
 * - ProfileService.getProfile() issues a GET /api/candidate-profile and returns the server’s JSON.
 * - Each modal (BasicInfoModal, QualificationsModal, etc.) writes back to Vuex directly.
 * - The Vuex module already has fields:
 *      state.basicInfo, state.qualification, state.experience, state.skills,
 *      state.resume, state.resumeUrl, state.audioIntro, state.audioIntroUrl,
 *      state.videoIntro, state.videoIntroUrl
 *   so that `profile` computed property reflects exactly what was returned by the server.
 */

const store = useStore();

const loading = ref(true);

// Modal‐toggle flags:
const showBasicInfoModal = ref(false);
const showQualificationsModal = ref(false);
const showExperienceModal = ref(false);
const showSkillsModal = ref(false);
const showResumeModal = ref(false);
const showDigitalProfileModal = ref(false);

// Grab “profile” from Vuex:
const profile = computed(() => store.state.profile);

const user = computed(() => store.state.auth.user || {});

console.log("Candidate Profile:", profile.value);

// computed URL for resume (either server‐provided or Blob)
const resumeUrl = computed(() => {
  // 1) If the server returned a URL, use that
  if (profile.value.resumeMeta?.url) return profile.value.resumeMeta.url;
  // 2) Otherwise if we have a Blob, create an object URL
  if (profile.value.resumeBlob) {
    return URL.createObjectURL(profile.value.resumeBlob);
  }
  return null;
});

// computed URL for audio (server URL or Blob)
const audioIntroUrl = computed(() => {
  if (profile.value.audioMeta?.url) return profile.value.audioMeta.url;
  if (profile.value.audioBlob)      return URL.createObjectURL(profile.value.audioBlob);
  return null;
});

// computed URL for video (server URL or Blob)
const videoIntroUrl = computed(() => {
  if (profile.value.videoMeta?.url) return profile.value.videoMeta.url;
  if (profile.value.videoBlob)      return URL.createObjectURL(profile.value.videoBlob);
  return null;
});


// Build lookups for job categories and industries:
const rawGroups = computed(() => store.getters["NewCategory/groups"] || []);
const jobCategoryLookup = computed(() => {
  return rawGroups.value.reduce((map, g) => {
    map[g.id] = g.title;
    return map;
  }, {});
});

const industryLookup = ref({});
// Assume you have an endpoint to fetch all industries:
onMounted(async () => {
  try {
    // 1) Fetch the candidate profile from backend:
    const response = await ProfileService.getProfile();
    // The API returns { status, message, data: {...CandidateProfile fields...} }
    const payload = response.data.data;
    console.log("Candidate Profile loaded:", payload);
    store.dispatch("profile/setCandidateProfile", payload);

    // 2) Fetch the industries list (for display in the Experience section):
    const r2 = await IndustryService.index();
    console.log("Industries loaded:", r2.data.data);
    const list = r2.data.data || [];
    const map = {};
    list.forEach((item) => {
      map[item.id] = item.name;
    });
    industryLookup.value = map;
  } catch (err) {
    console.error("Error loading Candidate Profile or industries:", err);
  } finally {
    loading.value = false;
  }
});

// PROGRESS & “SECTIONS REMAINING” LOGIC
const totalSections = 6;
const completedCount = computed(() => {
  let count = 0;
  const p = profile.value;

  // 1) Basic Info
  if (p.basicInfo && Object.keys(p.basicInfo).length > 0) count++;

  // 2) Qualifications
  if (p.qualification && Object.keys(p.qualification).length > 0) count++;

  // 3) Experience
  if (Array.isArray(p.experience) && p.experience.length > 0) count++;

  // 4) Skills
  if (Array.isArray(p.skills) && p.skills.length > 0) count++;

  // 5) Resume
  if (p.resumeMeta) count++;

  // 6) Digital Profile (audio or video)
  if (p.audioMeta || p.videoMeta) count++;

  return count;
});

const remainingSections = computed(() => totalSections - completedCount.value);

const remainingSectionsText = computed(() => {
  if (remainingSections.value === 0) {
    return "All sections complete";
  }
  return `${remainingSections.value} Sections Remaining`;
});

const progress = computed(() => {
  return (completedCount.value / totalSections) * 100;
});

// const progress = computed(() => {
//   let count = 0;
//   const p = store.state.profile;
//   if (
//     p.basicInfo?.email &&
//     p.basicInfo?.phoneNumber &&
//     p.basicInfo?.gender &&
//     p.basicInfo?.dob &&
//     p.basicInfo?.language &&
//     p.basicInfo?.visaStatus &&
//     p.basicInfo?.careerLevel &&
//     p.basicInfo?.salaryExpectation
//   ) {
//     count++;
//   }
//   if (
//     store.state.profile.qualification &&
//     store.state.profile.qualification.degree &&
//     store.state.profile.qualification.university &&
//     store.state.profile.qualification.graduationStart &&
//     store.state.profile.qualification.graduationEnd
//   ) {
//     count++;
//   }
//   if (store.state.profile.experience && store.state.profile.experience.length) {
//     count++;
//   }
//   if (store.state.profile.skills && store.state.profile.skills.length) {
//     count++;
//   }
//   if (p.resumeMeta) count++;
//   if (p.audioMeta || p.videoMeta) count++;

//   return (count / 6) * 100;
// });
</script>

<style scoped>
.candidate-profile-bg {
  background: #fafbfc;
  min-height: 100vh;
  padding: 32px 0;
}
.candidate-profile-root {
  max-width: 1200px !important;
  margin: 0 auto;
}
.profile-sidebar {
  background: transparent !important;
}
.sidebar-card {
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 4px 32px 0 rgba(89, 120, 255, 0.08);
  padding: 0 0 18px 0;
  overflow: hidden;
}
.sidebar-bg {
  background: linear-gradient(120deg, #e9f2fe 75%, #fff 100%);
  height: 108px;
  position: relative;
}
.sidebar-avatar {
  background: #fff;
  box-shadow: 0 2px 8px #e2eafc88;
  position: absolute;
  left: 50%;
  transform: translate(-50%, 34px);
  top: 0;
  z-index: 2;
}
.sidebar-content {
  padding-top: 56px;
  padding-left: 28px;
  padding-right: 28px;
}
.sidebar-name {
  font-size: 20px;
  font-weight: 700;
  text-align: center;
  margin-bottom: 16px;
}
.sidebar-progress-label {
  font-size: 15px;
  font-weight: 600;
  text-align: left;
  margin-bottom: 8px;
}
.sidebar-progress {
  margin-bottom: 20px;
}
.sidebar-sections {
  display: flex;
  flex-direction: column;
  gap: 7px;
}
.profile-main {
  background: transparent !important;
  padding-top: 10px;
}
.info-row {
  display: flex;
  gap: 32px;
  margin-bottom: 10px;
}
.q-degree {
  font-weight: bold;
}
.q-university,
.q-dates,
.q-country {
  color: #87a3c6;
  font-size: 14px;
  margin-bottom: 2px;
}
.exp-item {
  margin-bottom: 7px;
}
.text-muted {
  color: #bbb;
}
/* Make cards consistent with a 10px border radius */
.v-card {
  border-radius: 10px !important;
}

/* Disabled text style exactly as in the screenshot */
.text--disabled {
  color: rgba(0, 0, 0, 0.4) !important;
}
</style>
